<template>
    <v-textarea
        :label="entitiesLabel"
        variant="outlined"
        v-model="entitiesText"
        @update:modelValue="searchEntitiesText"
    ></v-textarea>
    <v-btn @click="reset" color="primary" size="x-small" class="mr-2">Reset</v-btn>
    <v-progress-circular
        v-if="showSpinner"
        :size="20"
        color="orange"
        indeterminate
    ></v-progress-circular>
    <v-alert color="warning" class="mt-2" v-if="errorMessage != undefined">{{ errorMessage }}</v-alert>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { debounce } from "@/lib/utils";
import { EntityLookupResult } from "@/services/commonModels";

const { entitiesLabel, entityLookupFn } = defineProps<{
    entitiesLabel: string;
    entityLookupFn: (codes: string[]) => Promise<EntityLookupResult>;
}>();

const emit = defineEmits<{
    (e: "entitiesFound", entities: EntityLookupResult);
}>();

const errorMessage = ref<string>();
const entitiesText = ref("");
const showSpinner = ref(false);

const searchEntitiesText = debounce(() => {
    searchEntities();
}, 500);

async function searchEntities() {
    const entityCodes = parseEntities();
    if (entityCodes.length > 0) {
        errorMessage.value = undefined;
        showSpinner.value = true;
        try {
            const result = await searchEntitiesRequest(entityCodes);
            if (!result.valid) {
                errorMessage.value = buildErrorMessage(result.invalidEntityCodes);
                emit("entitiesFound", { ...result, entities: [] });
            } else {
                errorMessage.value = undefined;
                emit("entitiesFound", { ...result, validEntityCodes: entityCodes });
            }
        } finally {
            showSpinner.value = false;
        }
    } else {
        errorMessage.value = undefined;
        emit("entitiesFound", emptyResult);
    }
}

async function searchEntitiesRequest(entityCodes: string[]) {
    return entityLookupFn(entityCodes);
}

function parseEntities() {
    return entitiesText.value
        .split(/,|\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

function buildErrorMessage(invalidEntityCodes: string[]) {
    if (invalidEntityCodes.length > 0) {
        return `Invalid entities: ${invalidEntityCodes.join(", ")}`;
    } else {
        return undefined;
    }
}

function reset() {
    entitiesText.value = "";
    errorMessage.value = undefined;
    showSpinner.value = false;
    emit("entitiesFound", emptyResult);
}

const emptyResult: EntityLookupResult = {
    valid: false,
    entities: [],
    invalidEntityCodes: [],
    validEntityCodes: [],
};
</script>
